import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Edit, Plus, Search, Trash2, X } from 'lucide-react';
import { Tutor, TutorStatus } from '@/types';
import { tutorApi } from '@/services/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { getTutorAvatarUrl } from '@/utils/tutorAvatar';

const statusClasses: Record<TutorStatus, string> = {
  Pending: 'border-amber-200 bg-amber-50 text-amber-800',
  Approved: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  Rejected: 'border-rose-200 bg-rose-50 text-rose-800',
};

export const AdminTutorsPage = () => {
  const navigate = useNavigate();
  const [tutors, setTutors] = useState<Tutor[]>([]);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [actionError, setActionError] = useState('');
  const [activeTutorId, setActiveTutorId] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchTutors = async () => {
      try {
        const tutorData = await tutorApi.getAllTutors({
          signal: controller.signal,
          scope: 'management',
        });
        setTutors(tutorData);
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Failed to fetch tutors:', error);
          setActionError((error as Error).message || 'Failed to load tutor profiles.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    void fetchTutors();
    return () => controller.abort();
  }, []);

  const filteredTutors = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return tutors;

    return tutors.filter((tutor) => {
      const expertise = (tutor.expertise || []).join(' ').toLowerCase();
      return (
        tutor.name.toLowerCase().includes(normalizedQuery) ||
        tutor.status.toLowerCase().includes(normalizedQuery) ||
        (tutor.title || '').toLowerCase().includes(normalizedQuery) ||
        expertise.includes(normalizedQuery) ||
        (tutor.linkedAccountEmail || '').toLowerCase().includes(normalizedQuery) ||
        (tutor.createdBy?.email || '').toLowerCase().includes(normalizedQuery)
      );
    });
  }, [query, tutors]);

  const pendingCount = tutors.filter((tutor) => tutor.status === 'Pending').length;

  const handleDelete = async (tutor: Tutor) => {
    if (!window.confirm('Delete this tutor profile? Courses will keep running but their tutor card will be cleared.')) {
      return;
    }

    try {
      setActionError('');
      setActiveTutorId(tutor.id);
      await tutorApi.deleteTutor(tutor.id);
      setTutors((prev) => prev.filter((item) => item.id !== tutor.id));
    } catch (error) {
      console.error('Failed to delete tutor:', error);
      setActionError((error as Error).message || 'Failed to delete tutor profile.');
    } finally {
      setActiveTutorId(null);
    }
  };

  const handleStatusChange = async (tutor: Tutor, status: TutorStatus) => {
    try {
      setActionError('');
      setActiveTutorId(tutor.id);
      const updatedTutor = await tutorApi.updateTutorStatus(tutor.id, status);
      setTutors((prev) => prev.map((item) => (item.id === tutor.id ? updatedTutor : item)));
    } catch (error) {
      console.error('Failed to update tutor status:', error);
      setActionError((error as Error).message || 'Failed to update tutor status.');
    } finally {
      setActiveTutorId(null);
    }
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="page-heading">Tutor Profiles</h1>
          <p className="page-subtitle">
            Review tutor submissions, track who created each profile, and control which profiles are approved for course use.
          </p>
          <p className="mt-2 text-sm text-gray-500">
            {pendingCount > 0 ? `${pendingCount} profile${pendingCount === 1 ? '' : 's'} pending approval.` : 'No profiles are waiting for approval.'}
          </p>
        </div>
        <Button variant="primary" size="md" onClick={() => navigate('/admin/tutors/new')}>
          <Plus className="mr-2 h-4 w-4" />
          Add Tutor
        </Button>
      </div>

      <Card className="mb-6 p-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by name, status, creator, or expertise..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="pl-12"
          />
        </div>
      </Card>

      {actionError ? <p className="mb-4 status-message border-red-100 bg-red-50 text-red-700">{actionError}</p> : null}

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse p-6">
              <div className="mb-2 h-6 w-1/3 rounded bg-gray-200" />
              <div className="h-4 w-2/3 rounded bg-gray-200" />
            </Card>
          ))}
        </div>
      ) : filteredTutors.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-gray-600">No tutors found</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredTutors.map((tutor) => {
            const isActive = activeTutorId === tutor.id;

            return (
              <Card key={tutor.id} className="p-6">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div className="flex min-w-0 flex-1 items-start gap-4">
                    <img
                      src={getTutorAvatarUrl(tutor)}
                      alt={tutor.name}
                      className="h-14 w-14 rounded-lg border border-slate-200 bg-slate-100 object-cover"
                      loading="lazy"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="truncate text-lg font-semibold text-gray-900">{tutor.name}</h3>
                        <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClasses[tutor.status]}`}>
                          {tutor.status}
                        </span>
                        {typeof tutor.yearsExperience === 'number' && tutor.yearsExperience > 0 ? (
                          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700">
                            {tutor.yearsExperience}+ yrs
                          </span>
                        ) : null}
                      </div>
                      {tutor.title ? <p className="mt-0.5 truncate text-sm text-gray-600">{tutor.title}</p> : null}
                      {tutor.bio ? <p className="mt-2 line-clamp-2 text-sm text-gray-600">{tutor.bio}</p> : null}
                      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-xs text-gray-500">
                        <span>Created by: {tutor.createdBy?.email || 'Unknown'}</span>
                        <span>Creator role: {tutor.createdBy?.role || 'Unknown'}</span>
                        <span>Linked account: {tutor.linkedAccountEmail || 'Not linked'}</span>
                      </div>
                      {tutor.expertise && tutor.expertise.length > 0 ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {tutor.expertise.slice(0, 6).map((tag) => (
                            <span
                              key={tag}
                              className="rounded-full border border-gray-200 bg-gray-100 px-2.5 py-1 text-xs text-gray-700"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={isActive || tutor.status === 'Approved'}
                      onClick={() => handleStatusChange(tutor, 'Approved')}
                      aria-label={`Approve ${tutor.name}`}
                    >
                      <Check className="h-4 w-4" />
                      <span className="hidden sm:inline">Approve</span>
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={isActive || tutor.status === 'Rejected'}
                      onClick={() => handleStatusChange(tutor, 'Rejected')}
                      aria-label={`Reject ${tutor.name}`}
                    >
                      <X className="h-4 w-4" />
                      <span className="hidden sm:inline">Reject</span>
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => navigate(`/admin/tutors/${tutor.id}/edit`)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="danger" size="sm" disabled={isActive} onClick={() => handleDelete(tutor)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
